import type {
  IAxisZoomBounds,
  IChartZoomPayload,
} from "../../../../../../components/common/chart-plugins/selection-zoom";

export interface INyquistPolarViewport {
  x: IAxisZoomBounds;
  y: IAxisZoomBounds;
}

const hasValidBounds = (bounds: IAxisZoomBounds) =>
  Number.isFinite(bounds.min) &&
  Number.isFinite(bounds.max) &&
  bounds.min < bounds.max &&
  Number.isFinite(bounds.max - bounds.min);

export const fitNyquistPolarViewport = (
  bounds: INyquistPolarViewport,
  width: number,
  height: number,
  fullDomain?: INyquistPolarViewport,
) => {
  const plotWidth = Number.isFinite(width) ? Math.max(1, width) : 1;
  const plotHeight = Number.isFinite(height) ? Math.max(1, height) : 1;
  const domain = fullDomain && hasValidBounds(fullDomain.x) && hasValidBounds(fullDomain.y) ? fullDomain : undefined;
  const requestedX = hasValidBounds(bounds.x) ? bounds.x : domain?.x ?? { min: -1, max: 1 };
  const requestedY = hasValidBounds(bounds.y) ? bounds.y : domain?.y ?? { min: -1, max: 1 };
  const requestedScale = Math.max(
    (requestedX.max - requestedX.min) / plotWidth,
    (requestedY.max - requestedY.min) / plotHeight,
  );
  const scale = domain
    ? Math.min(requestedScale, (domain.x.max - domain.x.min) / plotWidth, (domain.y.max - domain.y.min) / plotHeight)
    : requestedScale;
  const fitAxis = (requested: IAxisZoomBounds, size: number, fullBounds?: IAxisZoomBounds) => {
    const span = Math.min(size * scale, fullBounds ? fullBounds.max - fullBounds.min : Infinity);
    const center = requested.min / 2 + requested.max / 2;
    const start = center - span / 2;
    const min = fullBounds ? Math.max(fullBounds.min, Math.min(start, fullBounds.max - span)) : start;
    return { min, max: min + span };
  };
  return {
    x: fitAxis(requestedX, plotWidth, domain?.x),
    y: fitAxis(requestedY, plotHeight, domain?.y),
  };
};

export const getNyquistPolarZoomPayload = (
  payload: IChartZoomPayload,
  current: { x: IAxisZoomBounds; y: IAxisZoomBounds },
): IChartZoomPayload => {
  if (payload.axisMode !== "x" && payload.axisMode !== "y") return payload;
  const axis = payload.axisMode;
  const selected = axis === "x" ? payload.x : payload.y?.y;
  if (!selected) return payload;
  const ratio = (selected.max - selected.min) / (current[axis].max - current[axis].min);
  if (!Number.isFinite(ratio) || ratio <= 0) return payload;
  const other = axis === "x" ? "y" : "x";
  const center = (current[other].min + current[other].max) / 2;
  const halfSpan = ((current[other].max - current[other].min) * ratio) / 2;
  const matching = { min: center - halfSpan, max: center + halfSpan };
  return {
    axisMode: "xy",
    x: axis === "x" ? selected : matching,
    y: { y: axis === "y" ? selected : matching },
  };
};
